import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { Trans, useTranslation } from 'react-i18next';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useState } from 'react';

import { gql } from '__generated__';

import { CopyLinkInput } from '../../common/Common/CopyLink';
import { EditAppPermissionsForm } from './EditPermissionsForm';
import { getServerErrors } from '../../common/helpers';
import { useNotifier } from '../../common/Notifications/NotificationProvider';
import GetAppsForProjectQuery from './GetAppsForProjectQuery';
import UI from '../../common/UI';
import useMutation from '../../api/useMutation';
import useProject from '../useProject';
import useValidation from '../../common/useValidation';

const addAppSchema = yup.object().shape({
  name: yup.string().required(),
  permissions: yup.array().min(1).required(),
});

export const AuthorizeAppToProjectMutation = gql(`
  mutation AuthorizeAppToProject($project_id: ID!, $name: String!, $permissions: [Permission!]!) {
    authorizeAppToProject(project_id: $project_id, name: $name, permissions: $permissions) {
      token
    }
  }
`);

export interface AddAppFormProps {
  onSuccess?: (token: string) => void;
  onCancel?: () => void;
  refetchQueries?: DocumentNode[];
}

export const AddAppForm = ({ onSuccess, onCancel, refetchQueries = [GetAppsForProjectQuery] }: AddAppFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const project = useProject();

  const form = useForm({
    values: {
      name: '',
      permissions: [],
    },
  });

  const [addApp, { error, loading }] = useMutation(
    AuthorizeAppToProjectMutation, {
      variables: {
        project_id: project.id,
        ...form.values,
      },
      onCompleted: ({ authorizeAppToProject: { token } }) => {
        notifier.success(t('notifications.app_added'));
        onSuccess?.(token);
      },
      refetchQueries,
      awaitRefetchQueries: refetchQueries.length > 0,
    },
  );

  const { valid, errors } = useValidation({
    schema: addAppSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => addApp()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>
            {t('add_app')}
          </UI.H4>
          <UI.Paragraph sc={{ mb: 0 }}>
            {t('add_app_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.InputGroup
          sc={{ valid: !errors.name, touched: form.touched.name, mb: 0 }}
        >
          <UI.InputLabel htmlFor="Name">{t('name')}</UI.InputLabel>
          <UI.InputDescription>
            {t('app_name_description')}
          </UI.InputDescription>
          <UI.DebouncedInput id="Name" {...form.text('name')} />
          <UI.ErrorMessages attribute={t('name')} errors={errors.name} />
        </UI.InputGroup>

        <EditAppPermissionsForm form={form} errors={errors} />

        <UI.HR />

        <UI.Div sc={{ textAlign: 'right' }}>
          {onCancel && (
            <UI.Button onClick={() => onCancel()} sc={{ mr: 2 }}>
              {t('cancel')}
            </UI.Button>
          )}
          <UI.Button
            type="submit"
            sc={{ brand: 'secondary', loading }}
            disabled={!valid || loading}
          >
            {t('add_app')}
          </UI.Button>
        </UI.Div>
      </UI.FormGrid>
    </UI.Form>
  );
};

interface AppAddedProps {
  token: string;
  onSuccess?: () => void;
}

const AppAdded = ({ token, onSuccess }: AppAddedProps) => {
  const { t } = useTranslation();

  return (
    <UI.Div>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4>
            {t('access_token')}
          </UI.H4>
        </UI.Div>

        <UI.Div>
          <Trans i18nKey="app_access_token_description">
            <UI.Strong />
          </Trans>
        </UI.Div>

        <UI.InputGroup>
          <CopyLinkInput value={token} />
        </UI.InputGroup>

        <UI.Warning>
          <Trans i18nKey="app_access_token_warning">
            <UI.Strong />
          </Trans>
        </UI.Warning>

        <UI.HR />

        <UI.Div sc={{ textAlign: 'right' }}>
          <UI.Button
            type="submit"
            sc={{ brand: 'secondary' }}
            onClick={() => onSuccess?.()}
          >
            {t('ok')}
          </UI.Button>
        </UI.Div>
      </UI.FormGrid>
    </UI.Div>
  );
};

export interface AddAppModalProps {
  closeModal: () => void;
  refetchQueries?: DocumentNode[];
}

export const AddAppModal = ({ closeModal, refetchQueries }: AddAppModalProps) => {
  const [token, setToken] = useState<string | null>(null);

  return !token
    ? <AddAppForm onSuccess={setToken} onCancel={closeModal} refetchQueries={refetchQueries} />
    : <AppAdded token={token} onSuccess={closeModal} />;
};

const useAddAppModal = () => {
  const [openModal, closeModal] = useModal(() => (
    <UI.Modal maxWidth={600} close={closeModal}>
      <AddAppModal closeModal={closeModal} />
    </UI.Modal>
  ), []);

  return [openModal, closeModal];
};

export default useAddAppModal;
